import { StorageAdapter } from '../storage/StorageAdapter'
import { newId } from '../repositories/ids'
import { WebIdbBackend } from './backends/WebIdbBackend'
import { NativeFsBackend } from './backends/NativeFsBackend'
import { isNativePlatform } from '../storage/platform'
import { decodedByteLength } from './base64'
import { dbg, dbgError } from '../logger'

/**
 * PhotoStore — captura de fotos/firmas/evidencia en la capa offline-v2 (MOD-04/05).
 *
 * El binario (base64 dataURL) se escribe FUERA de SQLite via el backend activo:
 *   - nativo: @capacitor/filesystem (archivo bajo Directory.Data por ruta RELATIVA),
 *   - web:    idb-keyval (clave = image_ref).
 * SQLite SOLO guarda la fila de ciclo de vida en photo_meta (image_ref/local_path/upload_status) —
 * NUNCA los bytes (MOD-04, schema.js:16). El binario se purga tras subida confirmada (purge.js).
 *
 * El backend es INYECTABLE (Pitfall 5): los gates corren en node y pasan WebIdbBackend (sobre
 * fake-indexeddb) o un MemoryBackend; el nativo se difiere a F7 (device). NUNCA se importa el
 * plugin nativo top-level (NativeFsBackend hace import lazy dentro de sus metodos).
 *
 * SQL SOLO via el `db` inyectado (default = StorageAdapter, STOR-05).
 */

/**
 * Backend de fotos por defecto, resuelto en CALL-TIME (fix #3.1 del audit), no en import-time.
 *
 * Antes: `export const defaultBackend = isNativePlatform() ? Native : Web` se evaluaba al importar el
 * modulo. Si PhotoStore se importaba ANTES de que el bridge nativo estuviera listo, el
 * backend quedaba FIJADO a WebIdbBackend para toda la sesion -> en nativo las fotos irian a
 * idb-keyval (sin durabilidad dura, read por local_path roto, purga al store equivocado).
 *
 * Ahora se resuelve la PRIMERA vez que se necesita (capture/resolveBlob/purge) via `getDefaultBackend`
 * y se MEMOIZA tras la primera deteccion exitosa, eliminando la dependencia del orden de import.
 * La deteccion usa el helper UNIFICADO `isNativePlatform` (mismo origen Capacitor.isNativePlatform() que
 * StorageAdapter, fix #3.2). Sigue siendo INYECTABLE: capture/purge/resolveBlob aceptan `backend`.
 */
let _backend = null
export function getDefaultBackend () {
  if (_backend) return _backend // memoizado tras primera deteccion
  _backend = isNativePlatform() ? NativeFsBackend : WebIdbBackend
  return _backend
}

/**
 * Captura un binario base64 y crea su fila photo_meta(pending). NUNCA escribe el binario en SQLite.
 *
 * @param {string} base64DataUrl  dataURL de la foto/firma (FieldUploadEditPicture.vue / signature_pad).
 * @param {{ parentUuid?: string, parentKind?: string, itemId?: number }} meta
 *        parentUuid = client_uuid del padre (answer/addon/signature/evidence/task) cuyo op sube
 *        esta foto; parentKind = 'answer'|'addon'|'signature'|'evidence'|...; itemId = item del form.
 * @param {{ backend?: object, db?: object }} deps  backend/db inyectables (default = activos).
 * @returns {Promise<{ image_ref: string, local_path: string|null }>}
 */
export async function capture (
  base64DataUrl,
  { parentUuid, parentKind, itemId } = {},
  { backend = getDefaultBackend(), db = StorageAdapter } = {}
) {
  // GUARD DE ENTRADA (integridad v9): basura no entra al pipeline. base64ToBlob NUNCA lanza (devuelve
  // Blob vacío ante datos no decodificables) — este throw es la única barrera; cae en los catch de los
  // callers (las actions de occurrences), que ya llaman reportPhotoCaptureFailed -> nota + aviso de re-toma.
  const byteLength = decodedByteLength(base64DataUrl)
  if (!byteLength) {
    throw new Error('[offline-v2] capture: binario vacío o no decodificable')
  }
  const imageRef = newId() // ref estable; se usa como image_ref en answers/addons/photo_meta
  // ATOMICIDAD (anti fuga silenciosa): se SELLA primero la fila de intención en photo_meta y DESPUÉS
  // se escribe el binario. Si el proceso muere ENTRE ambos pasos, queda una fila pending SIN binario
  // (parent_uuid puesto, sin op en outbox) que sweepOrphanPhotos detecta pasado el grace y reporta
  // como foto huérfana ("vuelve a tomarla") — en vez de un binario huérfano en disco sin ningún rastro
  // en la BD, invisible para purga y sweep, y sin aviso al usuario (el orden inverso lo perdía en
  // silencio). byte_length (v9) = bytes DECODIFICADOS esperados: la vara contra la que
  // resolveBlob/resolveBase64 verifican integridad al leer (un binario que no la mida es un truncado y
  // se trata como AUSENTE). El binario NUNCA vive en SQLite (MOD-04); local_path se confirma tras write.
  await db.run(
    'INSERT INTO photo_meta (image_ref,parent_uuid,parent_kind,item_id,local_path,byte_length,upload_status,created_at) ' +
    'VALUES (?,?,?,?,?,?,?,?) ' +
    'ON CONFLICT(image_ref) DO UPDATE SET ' +
    'local_path=excluded.local_path,byte_length=excluded.byte_length,upload_status=excluded.upload_status,' +
    'parent_uuid=excluded.parent_uuid,parent_kind=excluded.parent_kind,item_id=excluded.item_id',
    [imageRef, parentUuid ?? null, parentKind ?? null, itemId ?? null, null, byteLength, 'pending', new Date().toISOString()]
  )
  // Escribe el BINARIO via el backend (nativo: ruta relativa; web: localPath=null).
  let localPath
  try {
    localPath = await backend.write(imageRef, base64DataUrl)
  } catch (err) {
    // La escritura falló: borra la fila de intención para no dejar una huérfana espuria; el caller
    // (las actions de occurrences) ya reporta captura fallida + aviso de re-toma vía este throw.
    try { await db.run('DELETE FROM photo_meta WHERE image_ref=?', [imageRef]) } catch { /* best-effort */ }
    dbgError('photo', `write FALLÓ (ref=${imageRef}, backend=${isNativePlatform() ? 'native-fs' : 'web-idb'})`, err)
    throw err
  }
  // Confirma la ruta del binario recién escrito (nativo). En web local_path queda null (idb-keyval).
  await db.run('UPDATE photo_meta SET local_path=? WHERE image_ref=?', [localPath ?? null, imageRef])
  dbg('photo', `capturada ref=${imageRef} kind=${parentKind ?? '?'} item=${itemId ?? '?'} -> ${localPath || '(web-idb)'} (${byteLength} bytes)`)
  return { image_ref: imageRef, local_path: localPath }
}

/**
 * Re-apunta el parent_uuid de una fila photo_meta ya capturada (MOD-05, fix purga locked [06-02]).
 *
 * Se usa cuando el client_uuid ESTABLE del padre solo se conoce DESPUES de capturar (caso answers:
 * AnswersRepository.saveAnswer resuelve/devuelve el client_uuid estable por (occurrence_id,item_id)
 * recien al guardar). Sin esto, parent_uuid quedaria != client_uuid del op de outbox que sube la
 * foto y el predicado de purga (purge.js) NUNCA emparejaria -> el binario jamas se purga.
 *
 * Idempotente y solo-referencia: actualiza UNA columna de photo_meta; NUNCA toca el binario. SQL
 * SOLO via el `db` inyectado (default = StorageAdapter, STOR-05).
 *
 * @param {string} imageRef    image_ref de la fila photo_meta a re-apuntar.
 * @param {string} parentUuid  client_uuid ESTABLE del padre (el del op de outbox que sube la foto).
 * @param {{ db?: object }} deps
 * @returns {Promise<void>}
 */
export async function reparent (imageRef, parentUuid, { db = StorageAdapter } = {}) {
  if (!imageRef || !parentUuid) return
  await db.run('UPDATE photo_meta SET parent_uuid=? WHERE image_ref=?', [parentUuid, imageRef])
}

/**
 * Dado un set de image_refs cuyo binario NO se pudo resolver al armar el envio, devuelve SOLO los
 * que NO tienen subida confirmada (upload_status != 'done', o sin fila en photo_meta) — es decir,
 * los que representan una PERDIDA GENUINA (binario evictado/corrompido ANTES de subirse), no una
 * purga legitima post-subida.
 *
 * Semantica (ver purge.js): la purga setea upload_status='done' DESPUES de borrar el binario de una
 * foto ya subida. Por tanto:
 *   - upload_status='done'  -> binario subido y purgado -> omitirlo es SEGURO (el server lo tiene).
 *   - upload_status!='done' / sin fila -> binario desaparecio sin subirse -> PERDIDA -> reportar.
 *
 * SOLO LECTURA y solo-referencia; SQL via el `db` inyectado (default = StorageAdapter, STOR-05).
 *
 * @param {Array<string>} imageRefs
 * @param {{ db?: object }} deps
 * @returns {Promise<Array<string>>} subconjunto de imageRefs sin subida confirmada.
 */
export async function unconfirmedRefs (imageRefs, { db = StorageAdapter } = {}) {
  const refs = (imageRefs || []).filter(Boolean)
  if (!refs.length) return []
  const placeholders = refs.map(() => '?').join(',')
  const result = await db.query(
    `SELECT image_ref, upload_status FROM photo_meta WHERE image_ref IN (${placeholders})`,
    refs
  )
  const rows = (result && result.values) || []
  const confirmed = new Set(rows.filter(r => r.upload_status === 'done').map(r => r.image_ref))
  // refs sin fila en photo_meta NO estan en `confirmed` -> caen como no-confirmadas (perdida).
  return refs.filter(ref => !confirmed.has(ref))
}

export const PhotoStore = { capture, reparent, getDefaultBackend, unconfirmedRefs }
